import {
  SURVEY_EXPIRATION_NAME,
  SURVEY_STATE_NAME,
  SURVEY_STATE_VALUE_ANSWERED,
  SURVEY_STATE_VALUE_DECLINED,
} from './constants';
import storage from '../../common/storage';
import hash from './hash';
import locations from './locations';

// from phase 1
/**
 * In case the search query `rexsurvey_save_state=false` in `location.search`:
 *
 * 1. Clear current survey state and expiration time in localStorage, both for (A) and (B)
 * 2. Don't store status into localStorage
 * @return {boolean} true: rexsurvey_save_state=false in query -> clear localstorage
 * @see https://confluence.rakuten-it.com/confluence/display/UEIT/PoPAsk%3A+phase2+-+Rule+for+refrain+the+survey+frequency
 */
const shouldStoreState = () =>
  /rexsurvey_save_state=false/.test(window.location.search) === false;

/**
 * local storage name を生成します
 * @param {string} base local storage default name {@link SURVEY_EXPIRATION_NAME} or {@link SURVEY_STATE_NAME}
 * @param {string} hashStr `config.enableDisplaySuppressionPerPage` : true 時に付与される hash - {@link hash}
 * @return {string} local storage key name を返します
 */
const keyName = (base, hashStr) => (hashStr ? `${base}-${hashStr}` : base);

/**
 * local storage {@link SURVEY_EXPIRATION_NAME} and {@link SURVEY_STATE_NAME} を clear します
 * @param {string} hashStr local storage key へ付与する hash value
 */
const clearAll = hashStr => {
  storage.remove(keyName(SURVEY_EXPIRATION_NAME, hashStr));
  storage.remove(keyName(SURVEY_STATE_NAME, hashStr));
};

/**
 * local storage {@link SURVEY_STATE_NAME} 値を取得し {@link SURVEY_STATE_VALUE_ANSWERED} or {@link SURVEY_STATE_VALUE_DECLINED} を調べます
 * @param {string} hashStr local storage key へ付与する hash value
 * @return {boolean} true: display widget
 */
const answer = hashStr => {
  const value = storage.get(keyName(SURVEY_STATE_NAME, hashStr));
  return (
    value !== SURVEY_STATE_VALUE_ANSWERED &&
    value !== SURVEY_STATE_VALUE_DECLINED
  );
};

/**
 * `SURVEY_EXPIRATION_NAME` value が期限内かを調べます
 * @param {string} hashStr local storage key へ付与する hash value
 * @return {boolean} true: 期限を過ぎでいる
 */
const expire = hashStr => {
  const time = storage.get(keyName(SURVEY_EXPIRATION_NAME, hashStr));
  if (!time) {
    // not found `SURVEY_EXPIRATION_NAME`
    return true;
  }
  const expireTime = parseInt(time, 10);
  return isNaN(expireTime) || (expireTime >= 0 && expireTime < Date.now());
};

const has = hashStr => storage.has(keyName(SURVEY_EXPIRATION_NAME, hashStr));

/**
 * local storage {@link SURVEY_EXPIRATION_NAME} 値を取得し期限内を調べます
 * - 期限内の時は {@link SURVEY_STATE_VALUE_ANSWERED} を調べるために {@link answer} 実行します
 * @param {string} hashStr local storage key へ付与する hash value
 * @return {boolean} true: display widget
 */
const expireWithAnswer = hashStr => {
  const time = storage.get(keyName(SURVEY_EXPIRATION_NAME, hashStr));
  if (!time) {
    // not found `SURVEY_EXPIRATION_NAME`
    return true;
  }
  const expireTime = parseInt(time, 10);
  if (isNaN(expireTime) || expireTime < Date.now()) {
    // illegal value or infinite or until limit
    return answer(hashStr);
  }
  // do not show
  return false;
};

/**
 * config.enableDisplaySuppressionPerPage true の時に {@link hash} `btoa` で pathname から hash value 生成します
 * @param {boolean} enableDisplaySuppressionPerPage true: create hash
 * @return {string} hash value or empty string
 */
const makeHash = enableDisplaySuppressionPerPage =>
  // config.enableDisplaySuppressionPerPage: true の時に locations.pathname から hash 生成する
  enableDisplaySuppressionPerPage ? hash(locations.pathname()) : '';

/**
 * localstorage をチェックし survey 表示判定します
 * @param {boolean} enableDisplaySuppressionPerPage true: create hash
 * @return {boolean} true: show survey
 */
const check = enableDisplaySuppressionPerPage => {
  // config.enableDisplaySuppressionPerPage: true の時に locations.pathname から hash 生成する
  const hashStr = makeHash(enableDisplaySuppressionPerPage);
  return expireWithAnswer(hashStr);
};

/**
 * 保存する日数 を milli second 変換します
 * @param {number} expirationPeriod config.expirationPeriod 保存する日数
 * @return {number} milli second 変換し返します
 */
const ms = expirationPeriod =>
  expirationPeriod < 0
    ? -1
    : Date.now() + expirationPeriod * 1000 * 60 * 60 * 24;

/**
 * LocalStorageに保存する - {@link SURVEY_STATE_NAME} / {@link SURVEY_EXPIRATION_NAME}
 * @param {number} expirationPeriod config.expirationPeriod 保存する日数
 * @param {boolean} enableDisplaySuppressionPerPage config.enableDisplaySuppressionPerPage pathname 単位で保存する flag
 * @param {string} value 保存するデータ
 */
const store = (expirationPeriod, enableDisplaySuppressionPerPage, value) => {
  const hashStr = makeHash(enableDisplaySuppressionPerPage);
  storage.save(value, keyName(SURVEY_STATE_NAME, hashStr));
  if (expirationPeriod < 0) {
    storage.save(-1, keyName(SURVEY_EXPIRATION_NAME, hashStr));
  } else {
    storage.save(
      ms(expirationPeriod),
      keyName(SURVEY_EXPIRATION_NAME, hashStr)
    );
  }
};

/**
 * Surveyへ回答した」という状態をLocalStorageに保存する
 * @param {number} expirationPeriod config.expirationPeriod 保存する日数
 * @param {boolean} enableDisplaySuppressionPerPage config.enableDisplaySuppressionPerPage pathname 単位で保存する flag
 */
const storeAnswer = (expirationPeriod, enableDisplaySuppressionPerPage) =>
  store(
    expirationPeriod,
    enableDisplaySuppressionPerPage,
    SURVEY_STATE_VALUE_ANSWERED
  );

/**
 * Surveyへの回答を拒否した」という状態をLocalStorageに保存する
 * @param {number} expirationPeriod config.expirationPeriod 保存する日数
 * @param {boolean} enableDisplaySuppressionPerPage config.enableDisplaySuppressionPerPage pathname 単位で保存する flag
 */
const storeDecline = (expirationPeriod, enableDisplaySuppressionPerPage) =>
  store(
    expirationPeriod,
    enableDisplaySuppressionPerPage,
    SURVEY_STATE_VALUE_DECLINED
  );

export default {
  check,
  expire,
  has,
  expireWithAnswer,
  answer,
  clearAll,
  makeHash,
  keyName,
  storeAnswer,
  storeDecline,
  shouldStoreState,
};



// WEBPACK FOOTER //
// ./src/phase2/survey/app/surveyState.js